<template>
  <div
    class="stage-slide"
    :class="`${slideData.customClass} ${cssTheme}`"
    :style="containerStyle"
  >
    <CdnImage
      v-if="slideData.background"
      v-bind="slideData.background"
      :lazy="lazyLoading"
      :preload="!lazyLoading"
      class="stage-slide__background"
    />
    <div class="stage-slide__container">
      <div class="stage-slide__information">
        <div class="stage-slide__headline-wrapper">
          <CdnImage
            v-if="brand"
            class="stage-slide__headline-brand filter-invert"
            :alt="brand.name"
            :src="brand.logo"
            :style="brand.style"
            :sizes="brand.sizes"
            render-without-figure
            :lazy="lazyLoading"
            :preload="!lazyLoading"
          />
          <Paragraph
            v-if="headlineText"
            :text="headlineText"
            class="stage-slide__headline"
          />
        </div>
        <p
          v-if="subline"
          class="stage-slide__subline"
        >
          <span v-html="subline"></span>
          <LinkButton
            v-if="slideData.modal?.opener?.includes('footnote')"
            tag="button"
            asterix
            type="button"
            aria-label="Fußnote"
            @click="openModalHandler"
          >
            *
          </LinkButton>
        </p>
      </div>
      <div
        v-if="visual"
        :class="hasVisualAlignment"
        class="stage-slide__visual-container"
      >
        <CdnImage
          v-if="visual.src"
          v-bind="visual"
          class="stage-slide__visual"
          :lazy="lazyLoading"
          :preload="!lazyLoading"
        />
        <SquareBadgeContainer
          v-if="slideData.squareBadge"
          :offer="offer || undefined"
          v-bind="slideData.squareBadge"
          class="stage-slide__square-badge"
        />
        <PromotionBadge
          v-if="slideData.badge"
          :badge="slideData.badge"
          :modal-texts="slideData.badgeTexts"
          :offer="offer"
        >
          <LinkButton
            v-if="slideData.modal?.opener?.includes('badge')"
            asterix
            tag="button"
            type="button"
            aria-label="Fußnote"
            @click="openModalHandler"
          >
            *
          </LinkButton>
        </PromotionBadge>
        <PricesContainer
          v-if="slideData.price && offer?.prices"
          class="stage-slide__prices"
          variant="medium"
          is-price-bold
          show-highlight
          :prices-data="offer.prices"
          :pre-text-is-price="slideData.price.preTextIsPrice"
          :pre-text-was-price="slideData.price.preTextWasPrice"
          :style="slideData.price.styles"
        />
        <Prices
          v-else-if="slideData.price?.default"
          class="stage-slide__prices"
          :is-price="slideData.price.default"
          :was-price="slideData.price?.was"
          is-price-bold
          show-highlight
          :pre-text-is-price="slideData.price.preTextIsPrice"
          :pre-text-was-price="slideData.price.preTextWasPrice"
          :style="slideData.price.styles"
        />
      </div>
      <div
        v-if="ctaButton || slideData.modal?.opener?.includes('button')"
        class="stage-slide__button-container"
      >
        <LinkButton
          v-if="ctaButton"
          v-bind="ctaButton"
          class="stage-slide__stage-link"
        />
        <LinkButton
          v-if="slideData.secondaryCtaButton"
          v-bind="slideData.secondaryCtaButton"
          class="stage-slide__stage-link"
        />
        <LinkButton
          v-if="slideData.modal?.opener?.includes('button')"
          v-bind="slideData.modal?.openButton || undefined"
          :label="slideData.modal?.openButton?.label || 'Details'"
          tag="button"
          class="stage-slide__stage-link"
          type="button"
          @click="openModalHandler"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getPrice } from '@i22team/rocket';
import CdnImage from '~/components/cdn-image.vue';
import {
  Paragraph,
  LinkButton,
  Prices,
  type ModalButton,
} from '@i22team/td-longtail-vue-components';
import { openModal } from '~/composables/modal';
import { getMainImageFromOffer } from '~/lib/goliath-media';
import type { PromotionOffer } from '~/lib/offers-by-group';
import type { SlideVisual, StageSliderData } from '~/lib/promotions-type';
import { addLogo, getBrand } from '~/lib/brand';
import PricesContainer from '~/components/product-detail/prices-container.vue';
import PromotionBadge from '~/components/promotion-badge.vue';
import SquareBadgeContainer from '~/components/product-detail/square-badge-container.vue';

export type StageSlideComponentProps = {
  slideData: PromotionOffer<StageSliderData>;
  lazyLoading?: boolean;
};

const props = withDefaults(defineProps<StageSlideComponentProps>(), {});

const containerStyle = computed<string | undefined>(() => props.slideData?.customStyle);

const cssTheme = computed(() =>
  props.slideData.theme ? `theme--${props.slideData.theme}` : undefined
);

const offer = computed<ListOffer | undefined>(() => props.slideData.offer);

const defaultPrice = computed(() => {
  if (!offer.value?.prices) return undefined;
  const price = getPrice(offer.value.prices || [], 'DEFAULT', 'ONETIME');
  return price?.amount;
});

const wasPrice = computed(() => {
  if (!offer.value?.prices) return undefined;
  const price = getPrice(offer.value.prices || [], 'WAS', 'ONETIME');
  return price?.amount;
});

const headline = computed(() => props.slideData.headline);

const headlineText = computed(() => headline.value?.text ?? offer.value?.product?.name);

const brand = computed(() => {
  const headlineBrand = headline.value?.brand;
  if (headlineBrand?.slug) {
    const brandData = addLogo(headlineBrand);
    if (brandData) {
      return {
        ...brandData,
        ...headline.value?.brand,
        style: `aspect-ratio: ${brandData.aspectRatio};${headline.value?.brand?.style}`,
      };
    }
  }
  if (offer.value) {
    const brandData = getBrand(offer.value);
    if (brandData) {
      return {
        ...brandData,
        ...headline.value?.brand,
        style: `aspect-ratio: ${brandData.aspectRatio};${headline.value?.brand?.style}`,
      };
    }
  }

  return undefined;
});

const headingTag = computed(() => headline.value?.tag ?? 'h2');

const subline = computed(() => props.slideData.subline);

const slug = computed(() => offer.value?.product?.slug);

const ctaButton = computed(
  () =>
    ({
      label: 'Zum Angebot',
      to: slug.value ? `/geraete/${slug.value}` : undefined,
      ...props.slideData.ctaButton,
    }) as StageSliderData['ctaButton']
);

// srcMedium and srcLarge is possible as well
const visual = computed<SlideVisual & StageSliderData['visual']>(() => {
  const defaultVisual: SlideVisual = {
    src: undefined,
    verticalAlignment: 'center',
  };

  if (offer.value && !props.slideData.visual?.src) {
    const goliathImage = getMainImageFromOffer(offer.value, 0);
    if (goliathImage) {
      defaultVisual.src = goliathImage.small;
      defaultVisual.srcSm = goliathImage.medium;
      defaultVisual.srcMd = goliathImage.large;
      defaultVisual.aspectRatio = '1';
    }
  }

  return {
    ...defaultVisual,
    ...props.slideData.visual,
  };
});

const hasVisualAlignment = computed(() => {
  if (!visual.value.verticalAlignment) return 'stage-slide__visual-container--vertical-center';
  return `stage-slide__visual-container--vertical-${visual.value.verticalAlignment}`;
});

const openModalHandler = () => {
  if (!props.slideData.modal) return;
  const buttons: ModalButton[] = [
    {
      label: 'Schließen',
      tag: 'button',
      outline: true,
    },
  ];
  const modalBtn = props.slideData.modal.button;
  if (modalBtn) {
    buttons.push({
      label: modalBtn.label,
      tag: modalBtn.link?.startsWith('http') ? 'a' : 'nuxt-link',
      to: modalBtn.link,
      outline: true,
    });
  }
  openModal({
    component: {
      is: defineAsyncComponent(() => import('~/components/stage-slide-dialog.vue')),
      props: {
        slideData: props.slideData.modal,
      },
    },
    className: `stage-slide__modal stage-slide__modal--${props.slideData.modal?.id}`,
    buttons,
  });
};

defineExpose({
  containerStyle,
  cssTheme,
  hasVisualAlignment,
  defaultPrice,
  wasPrice,
  brand,
  headline,
  headlineText,
  headingTag,
  subline,
  ctaButton,
  visual,
  slug,
  openModalHandler,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.stage-slide {
  position: relative;
  display: grid;
  text-align: center;
  width: 100%;
  color: var(--color-font);
  background-color: var(--color-bg);

  @include mq($mq-sm) {
    justify-content: flex-start;
    text-align: left;
    min-height: initial;
  }
  @include mq($mq-md) {
    justify-content: center;
  }

  &__badge {
    position: absolute;
  }

  &__container,
  &__background {
    grid-row: 1;
    grid-column: 1;
  }

  &__background {
    display: block;
    width: 100vw;
    height: 100%;
    pointer-events: none;

    :deep(img) {
      object-fit: var(--background-fit-xs, cover);
      display: block;
      width: 100%;
      height: 100%;

      object-position: var(--background-position-xs, 50% 50%);

      @include mq($mq-sm) {
        object-position: var(--background-position-sm, 50% 50%);
        object-fit: var(--background-fit-sm, cover);
      }
      @include mq($mq-md) {
        object-position: var(--background-position-md, 50% 50%);
        object-fit: var(--background-fit-md, cover);
      }
    }
  }

  &__container {
    width: 100%;
    height: 100%;
    margin: 0 auto;
    padding-inline: 1rem;
    padding-top: $grid-base * 2;

    display: grid;
    gap: 1rem;
    grid-template-areas: 'information' 'visual' 'button';
    grid-template-rows: var(--slide-template-rows-xs, min-content auto min-content);

    @include mq($mq-sm) {
      gap: 2rem;
      grid-template-areas: 'information visual' 'button visual';
      grid-template-rows: var(--slide-template-rows-sm, auto auto);
      grid-template-columns: calc(100% - var(--visual-width, 60%)) var(--visual-width, 60%);
      align-items: var(--slide-align-items, center);
    }

    @include mq(375px) {
      max-width: 400px;
    }
    @include mq($mq-sm) {
      flex-direction: row;
      justify-content: initial;
      max-width: 95%;
      padding: 0;
    }
    @include mq($mq-md) {
      width: 100%;
      max-width: 90%;
    }
    @include mq(1200px) {
      max-width: 1000px;
    }
  }

  &__content {
  }

  &__button-container {
    align-self: var(--slide-button-align-self, start);
    grid-area: button;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    text-align: center;
    justify-content: center;
    margin-bottom: 1rem;

    @include mq($mq-sm) {
      text-align: left;
      justify-content: flex-start;
    }
  }

  &__button {
    position: relative;
  }

  &__headline {
    font-weight: 800;
    margin-bottom: $grid-base * 2;
    text-align: center;
    @include font-size(27px, 30px);

    @include mq($mq-sm) {
      text-align: left;
      @include font-size(36px, 38px);
    }
    @include mq($mq-md) {
      @include font-size(48px, 54px);
    }

    &-brand {
      height: 40px;
      display: inline-block;

      @include mq($mq-sm, $mq-md - 1) {
        height: 30px;
      }
    }
  }

  &__information {
    grid-area: information;
    align-self: var(--slide-information-align-self, end);

    @include mq(false, $mq-sm - 1) {
      padding-inline: 1rem;
    }
  }

  &__visual-container {
    grid-area: visual;
    align-self: var(--slide-visual-align-self);
    position: relative;
    margin-block: var(--slide-visual-margin-block-xs, 0);
    max-height: var(--stage-slide-height);

    &--vertical-center {
      text-align: center;
    }

    @include mq(false, $mq-sm - 1) {
      overflow: var(--slide-visual-overflow-xs, hidden);
    }

    @include mq($mq-sm) {
      height: 100%;
      margin-block: var(--slide-visual-margin-block-sm, 0);
      display: flex;
      justify-content: var(--slide-visual-jusity, center);

      &--vertical-center {
        align-items: center;
      }
      &--vertical-top {
        align-items: flex-start;
      }
      &--vertical-bottom {
        align-items: flex-end;
      }
    }
  }

  &__visual {
    display: flex;
    justify-content: var(--slide-visual-justify-xs, center);
    align-items: var(--slide-visual-align-xs, center);
    height: 100%;

    :deep(img) {
      display: block;
      width: 100%;
      object-fit: contain;
      object-position: center;

      @include mq(false, $mq-sm - 1) {
        height: var(--slide-image-height-xs, 100%);
      }

      @include mq($mq-sm) {
        height: var(--slide-image-height-sm, 80%);
      }

      @include mq($mq-md) {
        height: var(--slide-image-height-md, 100%);
      }
    }
  }

  &__square-badge {
    position: absolute;
    top: var(--slide-square-badge-xs-top, -1rem);
    left: var(--slide-square-badge-xs-left, 1rem);
    right: var(--slide-square-badge-xs-right, 1rem);
    bottom: var(--slide-square-badge-xs-bottom);

    @include mq($mq-sm) {
      top: var(--slide-square-badge-sm-top, 1rem);
      left: var(--slide-square-badge-sm-left, 1rem);
      right: var(--slide-square-badge-sm-right);
      bottom: var(--slide-square-badge-sm-bottom);
    }

    @include mq($mq-md) {
      top: var(--slide-square-badge-md-top, 1rem);
      left: var(--slide-square-badge-md-left, 1rem);
      right: var(--slide-square-badge-md-right);
      bottom: var(--slide-square-badge-md-bottom);
    }
  }

  &__subline {
    backdrop-filter: blur(4px);
    @include font-size(18px, 22px);

    @include mq($mq-sm) {
      @include font-size(21px, 24px);
    }
    @include mq($mq-md) {
      @include font-size(24px, 28px);
    }
  }

  &__modal {
    &-image {
      width: 270px;
      margin-bottom: 2 * $grid-base;
    }

    &-subheading {
      font-weight: 800;
      @include font-size(24px, 29px);
      text-align: center;
    }

    &-heading {
      color: var(--color-primary);
      margin-bottom: $grid-base * 2;
    }
  }

  &__prices {
    display: inline-block;
    margin-top: $grid-base * 2;

    @include mq($mq-sm) {
      margin-top: 50%;

      :deep(.prices__main) {
        flex-direction: column;
        grid-gap: 0;
      }
      :deep(.price) {
        flex-direction: row;
        flex-wrap: nowrap;
      }
    }
  }
}

.stage--samsung-angebote {
  .stage-slide {
    &__headline {
      &-brand {
        height: 24px;
      }
    }
  }
  .stage-slide__visual-container {
    @include mq(false, $mq-sm - 1) {
      display: none;
    }
  }
}

.stage--komoot {
  --background-fit-lg: contain;
  --background-position-sm: bottom center;
  --background-position-md: bottom center;
  --color-bg: #ede9de;

  :deep(.stage-slide__background img) {
    @include mq($mq-xl) {
      max-width: 1800px;
      margin-inline: auto;
    }
  }
}
</style>
